import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { subscribeSaved } from "@/lib/notifySaved";
import { cn } from "@/lib/utils";

export function SavedNotifier() {
  const [state, setState] = useState<{ message: string; visible: boolean; fading: boolean }>({
    message: "Saved",
    visible: false,
    fading: false,
  });

  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return subscribeSaved((msg = "Saved") => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (unmountTimer.current) clearTimeout(unmountTimer.current);

      setState({ message: msg, visible: true, fading: false });

      hideTimer.current = setTimeout(() => {
        setState((prev) => ({ ...prev, fading: true }));
        unmountTimer.current = setTimeout(() => {
          setState((prev) => ({ ...prev, visible: false, fading: false }));
        }, 350);
      }, 1500);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (unmountTimer.current) clearTimeout(unmountTimer.current);
    };
  }, []);

  if (!state.visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed bottom-4 right-4 z-[95] pointer-events-none select-none",
        "flex items-center gap-1.5 rounded-full border border-border/80 bg-card/95 px-3 py-1.5 shadow-md backdrop-blur-sm",
        "text-xs font-medium text-foreground transition-all duration-300 ease-in-out",
        state.fading ? "opacity-0 translate-y-1 scale-95" : "opacity-100 translate-y-0 scale-100"
      )}
    >
      <Check size={13} className="text-emerald-500 shrink-0" aria-hidden="true" />
      <span>{state.message}</span>
    </div>
  );
}
